import { Product } from "@/types/product";
import { Question } from "@/types/question";
import { ReadSurveyTemplate } from "@/types/survey-template";
import cloneQuestion from "./cloneQuestion";
import { PRODUCT_PLACEHOLDER } from "@/constants";

/**
 * Creates an array of questions based on a survey template.
 * If products are provided, creates the same questions from template for every product.
 * Any %PRODUKT% placeholder found in the question will be replaced with product name.
 */
export default function createQuestionsFromTemplate(
  template: ReadSurveyTemplate,
  products?: Product[],
): Question[] {
  if (products !== undefined && products.length > 0) {
    return products
      .map((product) => {
        const questions = cloneQuestionsWithRetainedRelationships(
          template.questions,
        );
        return replacePlaceholdersInQuestions(questions, product);
      })
      .flat();
  }
  return cloneQuestionsWithRetainedRelationships(template.questions);
}

function replacePlaceholdersInQuestions(
  questions: Question[],
  product: Product,
): Question[] {
  return questions.map((newQuestion) => ({
    ...newQuestion,
    question: newQuestion.question.replace(PRODUCT_PLACEHOLDER, product.name),
  }));
}

function cloneQuestionsWithRetainedRelationships(
  questions: Question[],
): Question[] {
  const transformedQuestionsIds = new Map<string, string>();

  function isDependentOnAlreadyTransformedQuestion(question: Question) {
    return (
      question.dependentOnQuestion !== undefined &&
      transformedQuestionsIds.has(question.dependentOnQuestion)
    );
  }

  function assignDependencyWithTransformedQuestion(question: Question) {
    if (!isDependentOnAlreadyTransformedQuestion(question)) {
      return question;
    }
    return {
      ...question,
      dependentOnQuestion: question.dependentOnQuestion
        ? transformedQuestionsIds.get(question.dependentOnQuestion)
        : undefined,
    };
  }

  function fixDependenciesOfTransformedQuestions(questions: Question[]) {
    return questions.map(assignDependencyWithTransformedQuestion);
  }

  const newQuestions = questions.map((question) => {
    const newQuestion = cloneQuestion(question);
    transformedQuestionsIds.set(question.id, newQuestion.id);
    return newQuestion;
  });

  return fixDependenciesOfTransformedQuestions(newQuestions);
}
